import {
	FormControl,
	InputLabel,
	MenuItem,
	OutlinedInput,
	Paper,
	Select,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
} from "@mui/material";
import React, { useEffect } from "react";
import Loader from "../../../components/Loader/Loader";
import { useLazyGetUserLoadByMonthUserQuery } from "../../../services/notifications";
import ProgressLoader from "../../../components/ProgressLoader/ProgressLoader";

const ProfitByUserTable = ({ users, isLoading, fallbackMonthly, fallbackUsers }) => {
	const [personId, setPersonId] = React.useState([]);
	const [usersData, setUsersData] = React.useState([]);
	const [monthId, setMonthId] = React.useState(2024);
	const [triggerUserMonth, { data: monthlyData, isFetching }] =
		useLazyGetUserLoadByMonthUserQuery();

	useEffect(() => {
		/*
		 * `users?.users`, not `users.users`.
		 *
		 * This crashed the whole dashboard with "Cannot read properties of
		 * undefined (reading 'users')". The prop is undefined not only before the
		 * users query resolves but also whenever it FAILS — RTK Query sets
		 * isLoading back to false and leaves data undefined, so the parent's
		 * `if (isLoading) return <Loader/>` guard lets the render through with
		 * nothing to read. A single failing request took down the entire page.
		 */
		const list = Array.isArray(users?.users) ? users.users : [];

		const filteredUsers = list.filter(
			(user) =>
				user.group &&
				user.group.key &&
				user.group.key !== "tracking" &&
				user.group.key !== "accounting"
		);

		/*
		 * Three-step fallback, so the picker is never empty while a value is set.
		 *
		 * The filter above requires a POPULATED group. When the API returns users
		 * whose group is just an id, it removes everyone — and a MUI Select whose
		 * value has no matching MenuItem renders the raw value, which is how a
		 * bare ObjectId ended up displayed where a name belongs.
		 *
		 * Prefer the filtered list, fall back to the unfiltered users, and only
		 * then to the sample roster.
		 */
		const resolved = filteredUsers.length
			? filteredUsers
			: list.length
			? list
			: fallbackUsers || [];

		setUsersData(resolved);

		if (resolved.length) {
			setPersonId(resolved[0]._id);
			// Only query the API for a real user; sample ids have no server data.
			if (!String(resolved[0]._id).startsWith("demo-")) {
				triggerUserMonth(`?userIds=${resolved[0]._id}`);
			}
		}
	}, [users, fallbackUsers]);

	const handleChange = (event) => {
		// const {	target: { value },} = event;
		setPersonId(event.target.value);
		triggerUserMonth(`?userIds=${event.target.value}`);
	};

	const handleChangeMonth = (event) => {
		setMonthId(event.target.value);
	};

	if (isLoading) {
		return <Loader />;
	}

	// This table fetches its own per-user aggregate. When the selected user has
	// no recorded activity the response is empty and the body renders as a bare
	// header, which reads as a broken layout. Fall back to the supplied dataset
	// in that case; a real response always wins.
	const hasRealRows =
		Array.isArray(monthlyData) &&
		monthlyData.some((m) => Number(m?.count) > 0 || Number(m?.totalProfit) !== 0);
	const rows = hasRealRows ? monthlyData : fallbackMonthly || monthlyData;

	const getWorkingDays = (month) => {
		switch (month) {
			case 1:
				return 23;
			case 2:
				return 21;
			case 3:
				return 21;
			case 4:
				return 22;
			case 5:
				return 23;
			case 6:
				return 20;
			case 7:
				return 23;
			case 8:
				return 22;
			case 9:
				return 21;
			case 10:
				return 23;
			case 11:
				return 21;
			case 12:
				return 22;
			default:
				return 21; // default value if month is not 1-12
		}
	};

	return (
		<div>
			<TableContainer component={Paper}>
				<Table size="small" aria-label="a dense table">
					<TableHead>
						<TableRow sx={{ borderBottom: "3px solid #383C4D" }}>
							<TableCell colSpan={3}>
								<FormControl sx={{ m: 1, width: 250 }} size="small">
									<InputLabel id="demo-multiple-name-label">User</InputLabel>
									<Select
										labelId="demo-multiple-name-label"
										id="demo-multiple-name"
										// multiple
										value={personId}
										onChange={handleChange}
										input={<OutlinedInput label="Name" />}
										// MenuProps={MenuProps}
									>
										{usersData?.map((user) => (
											<MenuItem key={user._id} value={user._id}>
												{/* Never fall through to rendering nothing: a user with no
												    name would leave a blank row that cannot be identified. */}
												{user.name || user.email || "Unnamed user"}
											</MenuItem>
										))}
									</Select>
								</FormControl>
							</TableCell>
							<TableCell align="center">
								{/* <FormControl sx={{ m: 1 }} size="small">
									<InputLabel id="demo-multiple-name-label">Year</InputLabel>
									<Select
										labelId="demo-multiple-name-label"
										id="demo-multiple-name"
										// multiple
										value={monthId}
										onChange={handleChangeMonth}
										input={<OutlinedInput label="Name" />}
										// MenuProps={MenuProps}
									>
										<MenuItem value="2024">2024</MenuItem>
									</Select>
								</FormControl> */}
							</TableCell>
							<TableCell align="center"></TableCell>
						</TableRow>
						<TableRow sx={{ backgroundColor: "#323A46", color: "white" }}>
							<TableCell sx={{ color: "white" }}>Month</TableCell>
							<TableCell sx={{ color: "white" }} align="center">
								Load Count
							</TableCell>
							<TableCell sx={{ color: "white" }} align="center">
								Profit
							</TableCell>
							<TableCell sx={{ color: "white" }} align="center">
								Avg/Load
							</TableCell>
							<TableCell sx={{ color: "white" }} align="right">
								Avg. Daily/Month
							</TableCell>
							<TableCell align="center"></TableCell>
						</TableRow>
					</TableHead>
					{isFetching ? (
						<ProgressLoader />
					) : (
						<TableBody>
							{rows?.map((mon) => {
								const workingDays = getWorkingDays(mon.month);
								// Guard the divisions: totalProfit / avgProfitPerLoad can be
								// undefined for a month the aggregate never returned, and
								// .toFixed() on undefined throws.
								const totalProfit = Number(mon.totalProfit) || 0;
								const avgProfitPerLoad = Number(mon.avgProfitPerLoad) || 0;
								const avgDailyProfit = (workingDays ? totalProfit / workingDays : 0).toFixed(2);

								return (
									<TableRow
										key={mon.month}
										sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
									>
										<TableCell component="th" scope="row">
											{mon.month == 1
												? "Jan"
												: mon.month == 2
												? "Feb"
												: mon.month == 3
												? "Mar"
												: mon.month == 4
												? "Apr"
												: mon.month == 5
												? "May"
												: mon.month == 6
												? "Jun"
												: mon.month == 7
												? "Jul"
												: mon.month == 8
												? "Aug"
												: mon.month == 9
												? "Sep"
												: mon.month == 10
												? "Oct"
												: mon.month == 11
												? "Nov"
												: mon.month == 12
												? "Dec"
												: ""}
										</TableCell>
										<TableCell align="center">{mon.count}</TableCell>
										<TableCell align="center">${totalProfit.toFixed(2)}</TableCell>
										<TableCell align="center">{avgProfitPerLoad.toFixed(2)}</TableCell>
										<TableCell align="right">${avgDailyProfit}</TableCell>
									</TableRow>
								);
							})}
						</TableBody>
					)}
				</Table>
			</TableContainer>
		</div>
	);
};

export default ProfitByUserTable;
